import { Dialog, DialogOverlay, DialogPortal } from '@/common/components/ui/dialog'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { ChevronLeftIcon, ChevronRightIcon, ExpandIcon, Plus, Trash2Icon, XIcon } from 'lucide-react'
import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import Spinner from '../Spinner'
import { replacePeriodToUnderscore } from '@/common/utils/transformers'

interface ImageCarouselProps {
  files: { id: string; file: File | string }[]
  name?: string,
  showRemoveButton?: boolean
  showNavigationButtons?: boolean
  showExpandButton?: boolean
  isLoading?: boolean
  handleRemoveImage?: (index: number) => void
  handleImageUpload?: (e: ChangeEvent<HTMLInputElement>) => Promise<void>
  inputFileIsDisabled?: boolean
  className?: string
  imageClassName?: string
  Placeholder?: JSX.Element
  maxDots?: number
}

function getImageSrc(file: File | string) {
  return typeof file === 'string' ? file : URL.createObjectURL(file)
}

export const ImageCarousel = ({
  files,
  name,
  showRemoveButton = true,
  showNavigationButtons = true,
  showExpandButton = false,
  handleRemoveImage,
  isLoading,
  handleImageUpload,
  inputFileIsDisabled,
  className,
  imageClassName,
  Placeholder,
  maxDots = 9
}: ImageCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isExpanded, setIsExpanded] = useState(false)

  const prevCount = useRef(files?.length)

  useEffect(() => {
    if (files?.length > prevCount.current) {
      setCurrentIndex(files?.length - 1)
    }
    prevCount.current = files?.length
  }, [files?.length])

  const hasPrev = currentIndex > 0
  const hasNext = currentIndex < files?.length - 1

  const goToPrev = () => {
    setCurrentIndex(hasPrev ? currentIndex - 1 : files?.length - 1)
  }

  const goToNext = () => {
    setCurrentIndex(hasNext ? currentIndex + 1 : 0)
  }

  const currentSrc = files?.[currentIndex]?.file && getImageSrc(files?.[currentIndex]?.file)

  const dotsTotal = files?.length ?? 0
  const dotsWindowSize = Math.min(maxDots, dotsTotal)
  const dotsStart = Math.max(
    0,
    Math.min(
      currentIndex - Math.floor(dotsWindowSize / 2),
      dotsTotal - dotsWindowSize
    )
  )

  if (isLoading) {
    return (
      <div className={twMerge("group relative size-72 flex justify-center items-center rounded-lg border overflow-hidden flex-shrink-0", className)}>
        <Spinner className="size-4 fill-white-800" />
      </div>
    )
  }

  if (!files?.length && Placeholder) {
    return Placeholder
  }

  return (
    <>
      <div className={twMerge('relative size-72 flex justify-center items-center rounded-lg border overflow-hidden flex-shrink-0', className)}>
        <img
          data-testid="image_preview"
          key={files?.[currentIndex]?.id}
          src={currentSrc}
          className={twMerge("object-cover w-full h-full rounded animate-fadeIn", imageClassName)}
          alt={`uploaded-${currentIndex}`}
        />

        <div className="absolute top-2 left-2 flex gap-1 transition">
          {!!handleImageUpload && (
            <>
              <label
                htmlFor={name}
                className={twMerge(
                  'bg-success hover:bg-success-hover p-0.5 rounded-full shadow transition cursor-pointer',
                )}
              >
                <Plus strokeWidth={1} className="size-7 text-white" />
              </label>
              <input type="file" name={name} disabled={inputFileIsDisabled} className="hidden" id={name} multiple onChange={handleImageUpload} data-testid={name ? replacePeriodToUnderscore(name) + '_floating_input' : ''} />
            </>
          )}
        </div>

        {showNavigationButtons && files?.length > 1 && (
          <>
            <button
              type="button"
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white-400 hover:bg-white-800 p-0.5 rounded-full shadow transition"
              onClick={goToPrev}
            >
              <ChevronLeftIcon strokeWidth={1} className="size-7 text-black" />
            </button>

            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white-400 hover:bg-white-800 p-0.5 rounded-full shadow transition"
              onClick={goToNext}
            >
              <ChevronRightIcon strokeWidth={1} className="size-7 text-black" />
            </button>
          </>
        )}

        <div className="absolute top-2 right-2 flex gap-1 transition z-20">
          {showExpandButton && (
            <button
              type="button"
              className="flex size-8 items-center justify-center bg-black-300 rounded-full text-white-800 transition hover:bg-black-600"
              onClick={() => setIsExpanded(true)}
            >
              <ExpandIcon strokeWidth={1} className="size-6" />
            </button>
          )}

          {showRemoveButton && (
            <button
              data-testid="remove_image_button"
              type="button"
              className="flex size-8 items-center justify-center bg-black-300 text-yellow-600 rounded-full text-white transition hover:bg-black-600"
              onClick={() => {
                handleRemoveImage?.(currentIndex)
                setCurrentIndex(currentIndex - 1 < 0 ? 0 : currentIndex - 1)
              }}
            >
              <Trash2Icon strokeWidth={1} className="size-5" />
            </button>
          )}
        </div>

        <div className="absolute bottom-4 flex justify-center items-center gap-2 px-4">
          {Array.from({ length: dotsWindowSize }, (_, index) => {
            const imgIndex = dotsStart + index
            const isActive = imgIndex === currentIndex
            const isFirstDot = index === 0
            const isLastDot = index === dotsWindowSize - 1
            const isEdge = (isFirstDot && dotsStart > 0) || (isLastDot && dotsStart + dotsWindowSize < dotsTotal)

            return (
              <button
                key={imgIndex}
                type="button"
                className={twMerge(
                  'size-2 rounded-full bg-white-300 border-[0.5px] border-[#444444] transition-all duration-200',
                  isEdge ? 'size-1.5' : 'size-2',
                  isActive && 'bg-blue-800',
                )}
                onClick={() => setCurrentIndex(imgIndex)}
              />
            )
          })}
        </div>
      </div>

      {showExpandButton && (
        <Dialog open={isExpanded} onOpenChange={setIsExpanded}>
          <DialogPortal>
            <DialogOverlay />
            <DialogPrimitive.Content className="fixed inset-0 z-50 flex items-center justify-center">
              <div className="relative flex items-center justify-center w-full h-full p-10">
                <div className="relative flex items-center justify-center w-[90vw] h-[90vh] max-w-[90vw] max-h-[90vh] bg-black-900 rounded-xl overflow-hidden">
                  <div className="absolute top-2 right-2 flex gap-2 z-10">
                    {showRemoveButton && (
                      <button
                        type="button"
                        className="flex size-8 items-center justify-center bg-yellow-op-2 hover:bg-yellow-hover rounded-full text-black transition"
                        onClick={() => {
                          handleRemoveImage?.(currentIndex)
                          const newIndex = currentIndex - 1 < 0 ? 0 : currentIndex - 1
                          setCurrentIndex(newIndex)
                          if (files?.length <= 1) setIsExpanded(false)
                        }}
                      >
                        <Trash2Icon strokeWidth={1} className="size-5" />
                      </button>
                    )}
                    <DialogPrimitive.Close className="flex size-8 items-center justify-center bg-white-400 hover:bg-white-800 rounded-full text-red-500 transition">
                      <XIcon strokeWidth={1} className="size-6" />
                    </DialogPrimitive.Close>
                  </div>

                  {files?.length > 1 && (
                    <>
                      <button
                        type="button"
                        className="absolute left-2 top-1/2 -translate-y-1/2 flex size-8 items-center justify-center bg-white-400 hover:bg-white-800 rounded-full shadow transition z-10"
                        onClick={goToPrev}
                      >
                        <ChevronLeftIcon strokeWidth={1} className="size-8 text-black" />
                      </button>
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 flex size-8 items-center justify-center bg-white-400 hover:bg-white-800 rounded-full shadow transition z-10"
                        onClick={goToNext}
                      >
                        <ChevronRightIcon strokeWidth={1} className="size-8 text-black" />
                      </button>
                    </>
                  )}

                  <img
                    key={files?.[currentIndex]?.id}
                    src={currentSrc}
                    className="max-w-full max-h-full min-w-full min-h-full object-contain rounded-lg animate-fadeIn"
                    alt={`expanded-${currentIndex}`}
                  />

                  <div className="absolute bottom-2 flex flex-wrap justify-center gap-2 px-4">
                    {files?.map((_, index) => (
                      <button
                        key={index}
                        type="button"
                        className={twMerge(
                          'size-3 rounded-full bg-white-300 border-[0.5px] border-[#444444]',
                          index === currentIndex && 'bg-blue-800',
                        )}
                        onClick={() => setCurrentIndex(index)}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </DialogPrimitive.Content>
          </DialogPortal>
        </Dialog>
      )}
    </>
  )
}
